import { useCallback, useRef, useState } from 'react';

export const useInfiniteScroll = (limit: number) => {
  const [offset, setOffset] = useState(0);
  const lastScrollPosition = useRef(0);
  const previousNodeRef = useRef<HTMLDivElement | null>(null);
  const scrollHandlerRef = useRef<((e: Event) => void) | null>(null);

  const handleScroll = useCallback(
    (node: HTMLDivElement | null) => {
      if (node) {
        const { scrollTop, clientHeight, scrollHeight } = node;
        // Math.ceil accounts for fractional scrollTop values from subpixel rendering
        const currentScrollPosition = Math.ceil(scrollTop + clientHeight);

        if (
          currentScrollPosition >= scrollHeight &&
          currentScrollPosition !== lastScrollPosition.current
        ) {
          setOffset((prevOffset) => prevOffset + limit);
          lastScrollPosition.current = currentScrollPosition;
        }
      }
    },
    [limit],
  );

  const viewportRef = useCallback(
    (node: HTMLDivElement | null) => {
      // Clean up previous node's listener
      if (previousNodeRef.current && scrollHandlerRef.current) {
        previousNodeRef.current.removeEventListener(
          'scroll',
          scrollHandlerRef.current,
        );
      }

      if (node) {
        const handler = () => handleScroll(node);
        scrollHandlerRef.current = handler;
        node.addEventListener('scroll', handler);
      } else {
        scrollHandlerRef.current = null;
      }

      previousNodeRef.current = node;
    },
    [handleScroll],
  );

  return { viewportRef, offset, setOffset };
};
